import './applicantEditResidencePage.scss';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { ThemeProvider } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import isEmpty from 'lodash/isEmpty';
import toString from 'lodash/toString';
import { ReactNode } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { AddressLineOne } from '../../../components/AddressLineOne/AddressLineOne';
import { Button } from '../../../components/Button/Button';
import { Header } from '../../../components/Header/Header';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { Select } from '../../../components/Select/Select';
import { inputEventHandler } from '../../../components/utils/inputEventHandler';
import { USStates } from '../../../store/addressSuggestionStore/constants/states';
import { housingTypeOptions } from '../../../store/applicantStore/constants/housingTypeOptions';
import { monthsAtResidenceOptions } from '../../../store/applicantStore/constants/monthsAtResidenceOptions';
import { ONE_YEAR_IN_MONTHS_AS_STRING } from '../../../store/applicantStore/constants/oneYear';
import useApplicantStore from '../../../store/applicantStore/useApplicantStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();

  const {
    computed: { reviewEditPathname, reviewEditProgress },
    currentResidenceCity,
    currentResidenceLineOne,
    currentResidenceLineTwo,
    currentResidenceMonthlyPaymentAmount,
    currentResidenceMonthsAt,
    currentResidencePostalCode,
    currentResidenceState,
    currentResidenceType,
    isSyncingResidence,
    previousResidenceCity,
    previousResidenceLineOne,
    previousResidenceLineTwo,
    previousResidencePostalCode,
    previousResidenceState,
    syncingResidenceFailedMessage,
    setCurrentResidenceCity,
    setCurrentResidenceLineOne,
    setCurrentResidenceLineTwo,
    setCurrentResidenceMonthlyPaymentAmount,
    setCurrentResidenceMonthsAt,
    setCurrentResidencePostalCode,
    setCurrentResidenceState,
    setCurrentResidenceType,
    setPreviousResidenceCity,
    setPreviousResidenceLineOne,
    setPreviousResidenceLineTwo,
    setPreviousResidencePostalCode,
    setPreviousResidenceState,
    syncResidence,
  } = useApplicantStore();

  const {
    control,
    register,
    handleSubmit,
    setValue,
    trigger,
    watch,
    formState: { errors, isDirty, isValid },
  } = useForm({
    criteriaMode: 'firstError',
    delayError: 200,
    defaultValues: {
      currentCityValue: currentResidenceCity,
      currentLineOneValue: currentResidenceLineOne,
      currentLineTwoValue: currentResidenceLineTwo,
      currentMonthsAtResidenceValue: currentResidenceMonthsAt,
      currentPostalCodeValue: currentResidencePostalCode,
      currentStateValue: currentResidenceState,
      housingAmountValue: inputEventHandler.currency.formatter(
        currentResidenceMonthlyPaymentAmount,
      ),
      housingTypeValue: currentResidenceType,
      previousCityValue: previousResidenceCity,
      previousLineOneValue: previousResidenceLineOne,
      previousLineTwoValue: previousResidenceLineTwo,
      previousPostalCodeValue: previousResidencePostalCode,
      previousStateValue: previousResidenceState,
    },
    mode: 'onBlur',
    shouldFocusError: false,
  });

  const shouldShowPreviousResidenceFields =
    watch('currentMonthsAtResidenceValue') === ONE_YEAR_IN_MONTHS_AS_STRING;

  const housingTypeValidation = {
    required: t('validation.housingType.required'),
  };

  const housingAmountValidation = {
    setValueAs: (value: string): string =>
      inputEventHandler.currency.parser(value),
    required: t('validation.housingAmount.required'),
    min: {
      value: 0,
      message: t('validation.housingAmount.min', { min: '$0' }),
    },
    max: {
      value: 1000000,
      message: t('validation.housingAmount.max', { max: '$1,000,000' }),
    },
  };

  const addressValidation = {
    required: t('validation.address.required'),
  };

  const cityValidation = {
    required: t('validation.city.required'),
  };

  const stateValidation = {
    required: t('validation.state.required'),
  };

  const postalCodeValidation = {
    required: t('validation.zipCode.required'),
    minLength: {
      value: 5,
      message: t('validation.zipCode.minMax', { minMax: 5 }),
    },
    maxLength: {
      value: 5,
      message: t('validation.zipCode.minMax', { minMax: 5 }),
    },
  };

  const monthsAtResidenceValidation = {
    required: t('validation.housingType.required'),
  };

  const finishEditing = (): void => {
    navigate(reviewEditPathname);
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const {
      currentCityValue,
      currentLineOneValue,
      currentLineTwoValue,
      currentMonthsAtResidenceValue,
      currentPostalCodeValue,
      currentStateValue,
      housingAmountValue,
      housingTypeValue,
      previousCityValue,
      previousLineOneValue,
      previousLineTwoValue,
      previousPostalCodeValue,
      previousStateValue,
    } = values;

    if (
      !currentCityValue ||
      !currentLineOneValue ||
      !currentMonthsAtResidenceValue ||
      !currentPostalCodeValue ||
      !currentStateValue ||
      !housingAmountValue ||
      !housingTypeValue
    ) {
      return;
    }

    if (
      shouldShowPreviousResidenceFields &&
      (!previousLineOneValue ||
        !previousCityValue ||
        !previousStateValue ||
        !previousPostalCodeValue)
    ) {
      return;
    }

    setCurrentResidenceCity(currentCityValue);
    setCurrentResidenceLineOne(currentLineOneValue);
    setCurrentResidenceLineTwo(currentLineTwoValue);
    setCurrentResidenceMonthlyPaymentAmount(housingAmountValue);
    setCurrentResidenceMonthsAt(currentMonthsAtResidenceValue);
    setCurrentResidencePostalCode(currentPostalCodeValue);
    setCurrentResidenceState(currentStateValue);
    setCurrentResidenceType(housingTypeValue);

    if (shouldShowPreviousResidenceFields) {
      setPreviousResidenceCity(previousCityValue);
      setPreviousResidenceLineOne(previousLineOneValue);
      setPreviousResidenceLineTwo(previousLineTwoValue);
      setPreviousResidencePostalCode(previousPostalCodeValue);
      setPreviousResidenceState(previousStateValue);
    } else {
      setPreviousResidenceCity(null);
      setPreviousResidenceLineOne(null);
      setPreviousResidenceLineTwo(null);
      setPreviousResidencePostalCode(null);
      setPreviousResidenceState(null);
    }

    if (isDirty) {
      const synced = await syncResidence();
      if (synced) {
        finishEditing();
      }

      return;
    }

    finishEditing();
  };

  return (
    <ThemeProvider theme={theme}>
      <Box className="applicant-edit-residence-page" sx={theme.pageLayout}>
        <Header
          progress={reviewEditProgress}
          showDrawerToggle
          onBackClick={finishEditing}
        />

        <main>
          <form
            data-testid={locator.pages.applicantEditResidence.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1 data-testid={locator.pages.applicantEditResidence.form.h1}>
                {t('ApplicantEditResidencePage.title')}
              </h1>
              <h2 data-testid={locator.pages.applicantEditResidence.form.h2}>
                {t('ApplicantEditResidencePage.subtitle')}
              </h2>

              {!isEmpty(syncingResidenceFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={
                      locator.pages.applicantEditResidence.alert.error
                    }
                    icon={false}
                    severity="error"
                  >
                    {syncingResidenceFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <div>
                  <Select
                    control={control}
                    errorTestId={
                      locator.pages.applicantEditResidence.form
                        .residenceTypeSelectError
                    }
                    inputTestId={
                      locator.pages.applicantEditResidence.form
                        .residenceTypeSelect
                    }
                    label={t('ApplicantEditResidencePage.housingType')}
                    name="housingTypeValue"
                    options={housingTypeOptions}
                    rules={housingTypeValidation}
                  />
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <TextField
                    {...register('housingAmountValue', housingAmountValidation)}
                    error={!isEmpty(errors.housingAmountValue)}
                    label={t('ApplicantEditResidencePage.housingAmount')}
                    slotProps={{
                      htmlInput: {
                        'data-testid':
                          locator.pages.applicantEditResidence.form.amountInput,
                        inputMode: 'numeric',
                      },
                    }}
                    type="text"
                    variant="outlined"
                    fullWidth
                    onBlur={inputEventHandler.currency.blur(
                      'housingAmountValue',
                      setValue,
                      trigger,
                    )}
                    onChange={inputEventHandler.currency.change(
                      'housingAmountValue',
                      setValue,
                    )}
                    onFocus={inputEventHandler.currency.focus(
                      'housingAmountValue',
                      setValue,
                    )}
                    onKeyDown={inputEventHandler.number.keydown}
                    onPasteCapture={inputEventHandler.number.pasteCapture}
                  />

                  {!isEmpty(errors.housingAmountValue) && (
                    <span
                      className="field-error"
                      data-testid={
                        locator.pages.applicantEditResidence.form
                          .amountInputError
                      }
                    >
                      {toString(errors.housingAmountValue.message)}
                    </span>
                  )}
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <AddressLineOne
                    errorTestId={
                      locator.pages.applicantEditResidence.form
                        .addressLine1InputError
                    }
                    errors={errors}
                    label={t('ApplicantEditResidencePage.residenceLineOne')}
                    name="currentLineOneValue"
                    register={register}
                    setValue={setValue}
                    slotProps={{
                      htmlInput: {
                        'data-testid':
                          locator.pages.applicantEditResidence.form
                            .addressLine1Input,
                      },
                    }}
                    suggestionFields={{
                      cityFieldName: 'currentCityValue',
                      stateFieldName: 'currentStateValue',
                      postalCodeFieldName: 'currentPostalCodeValue',
                    }}
                    validation={addressValidation}
                    variant="outlined"
                    fullWidth
                  />
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <div className="inline-inputs">
                    <div className="input-half">
                      <TextField
                        {...register('currentLineTwoValue')}
                        autoComplete="address-line2"
                        label={t('ApplicantEditResidencePage.residenceLineTwo')}
                        slotProps={{
                          htmlInput: {
                            'data-testid':
                              locator.pages.applicantEditResidence.form
                                .addressLine2Input,
                          },
                        }}
                        variant="outlined"
                        fullWidth
                      />
                    </div>

                    <div className="input-half">
                      <TextField
                        {...register('currentCityValue', cityValidation)}
                        autoComplete="address-level2"
                        error={!isEmpty(errors.currentCityValue)}
                        label={t('ApplicantEditResidencePage.residenceCity')}
                        slotProps={{
                          inputLabel: {
                            shrink: Boolean(watch('currentCityValue')),
                          },
                          htmlInput: {
                            'data-testid':
                              locator.pages.applicantEditResidence.form
                                .cityInput,
                          },
                        }}
                        variant="outlined"
                        fullWidth
                      />

                      {!isEmpty(errors.currentCityValue) && (
                        <span
                          className="field-error"
                          data-testid={
                            locator.pages.applicantEditResidence.form
                              .cityInputError
                          }
                        >
                          {toString(errors.currentCityValue.message)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <div className="inline-inputs">
                    <div className="input-half">
                      <Select
                        control={control}
                        errorTestId={
                          locator.pages.applicantEditResidence.form
                            .stateSelectError
                        }
                        inputTestId={
                          locator.pages.applicantEditResidence.form.stateSelect
                        }
                        label={t('ApplicantEditResidencePage.residenceState')}
                        name="currentStateValue"
                        options={USStates.map(({ abbreviation, name }) => ({
                          name,
                          value: abbreviation,
                        }))}
                        rules={stateValidation}
                      />
                    </div>

                    <div className="input-half">
                      <TextField
                        {...register(
                          'currentPostalCodeValue',
                          postalCodeValidation,
                        )}
                        autoComplete="postal-code"
                        error={!isEmpty(errors.currentPostalCodeValue)}
                        label={t('ApplicantEditResidencePage.zip')}
                        slotProps={{
                          inputLabel: {
                            shrink: Boolean(watch('currentPostalCodeValue')),
                          },
                          htmlInput: {
                            'data-testid':
                              locator.pages.applicantEditResidence.form
                                .zipCodeInput,
                            inputMode: 'numeric',
                          },
                        }}
                        type="number"
                        variant="outlined"
                        fullWidth
                        onKeyDown={inputEventHandler.number.keydown}
                        onPasteCapture={inputEventHandler.number.pasteCapture}
                      />

                      {!isEmpty(errors.currentPostalCodeValue) && (
                        <span
                          className="field-error"
                          data-testid={
                            locator.pages.applicantEditResidence.form
                              .zipCodeInputError
                          }
                        >
                          {toString(errors.currentPostalCodeValue.message)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <Select
                    control={control}
                    errorTestId={
                      locator.pages.applicantEditResidence.form.monthSelectError
                    }
                    inputTestId={
                      locator.pages.applicantEditResidence.form.monthSelect
                    }
                    label={t('ApplicantEditResidencePage.duration')}
                    name="currentMonthsAtResidenceValue"
                    options={monthsAtResidenceOptions}
                    rules={monthsAtResidenceValidation}
                  />
                </div>
              </div>

              {shouldShowPreviousResidenceFields && (
                <div
                  data-testid={
                    locator.pages.applicantEditResidence.form
                      .previousResidenceRoot
                  }
                  style={{ marginBottom: '3rem' }}
                >
                  <h3>{t('ApplicantEditResidencePage.previousResidence')}</h3>

                  <div style={{ marginTop: '1.5rem' }}>
                    <AddressLineOne
                      errorTestId={
                        locator.pages.applicantEditResidence.form
                          .previousAddressLine1InputError
                      }
                      errors={errors}
                      label={t('ApplicantEditResidencePage.residenceLineOne')}
                      name="previousLineOneValue"
                      register={register}
                      setValue={setValue}
                      slotProps={{
                        htmlInput: {
                          'data-testid':
                            locator.pages.applicantEditResidence.form
                              .previousAddressLine1Input,
                        },
                      }}
                      suggestionFields={{
                        cityFieldName: 'previousCityValue',
                        stateFieldName: 'previousStateValue',
                        postalCodeFieldName: 'previousPostalCodeValue',
                      }}
                      validation={addressValidation}
                      variant="outlined"
                      fullWidth
                    />
                  </div>

                  <div style={{ marginTop: '1.5rem' }}>
                    <div className="inline-inputs">
                      <div className="input-half">
                        <TextField
                          {...register('previousLineTwoValue')}
                          autoComplete="address-line2"
                          label={t(
                            'ApplicantEditResidencePage.residenceLineTwo',
                          )}
                          slotProps={{
                            htmlInput: {
                              'data-testid':
                                locator.pages.applicantEditResidence.form
                                  .previousAddressLine2Input,
                            },
                          }}
                          variant="outlined"
                          fullWidth
                        />
                      </div>

                      <div className="input-half">
                        <TextField
                          {...register('previousCityValue', cityValidation)}
                          autoComplete="address-level2"
                          error={!isEmpty(errors.previousCityValue)}
                          label={t('ApplicantEditResidencePage.residenceCity')}
                          slotProps={{
                            inputLabel: {
                              shrink: Boolean(watch('previousCityValue')),
                            },
                            htmlInput: {
                              'data-testid':
                                locator.pages.applicantEditResidence.form
                                  .previousCityInput,
                            },
                          }}
                          variant="outlined"
                          fullWidth
                        />

                        {!isEmpty(errors.previousCityValue) && (
                          <span
                            className="field-error"
                            data-testid={
                              locator.pages.applicantEditResidence.form
                                .previousCityInputError
                            }
                          >
                            {toString(errors.previousCityValue.message)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div style={{ marginTop: '1.5rem' }}>
                    <div className="inline-inputs">
                      <div className="input-half">
                        <Select
                          control={control}
                          errorTestId={
                            locator.pages.applicantEditResidence.form
                              .previousStateSelectError
                          }
                          inputTestId={
                            locator.pages.applicantEditResidence.form
                              .previousStateSelect
                          }
                          label={t('ApplicantEditResidencePage.residenceState')}
                          name="previousStateValue"
                          options={USStates.map(({ abbreviation, name }) => ({
                            name,
                            value: abbreviation,
                          }))}
                          rules={stateValidation}
                        />
                      </div>

                      <div className="input-half">
                        <TextField
                          {...register(
                            'previousPostalCodeValue',
                            postalCodeValidation,
                          )}
                          autoComplete="postal-code"
                          error={!isEmpty(errors.previousPostalCodeValue)}
                          label={t('ApplicantEditResidencePage.zip')}
                          slotProps={{
                            inputLabel: {
                              shrink: Boolean(watch('previousPostalCodeValue')),
                            },
                            htmlInput: {
                              'data-testid':
                                locator.pages.applicantEditResidence.form
                                  .previousZipCodeInput,
                              inputMode: 'numeric',
                            },
                          }}
                          type="number"
                          variant="outlined"
                          fullWidth
                          onKeyDown={inputEventHandler.number.keydown}
                          onPasteCapture={inputEventHandler.number.pasteCapture}
                        />

                        {!isEmpty(errors.previousPostalCodeValue) && (
                          <span
                            className="field-error"
                            data-testid={
                              locator.pages.applicantEditResidence.form
                                .previousZipCodeInputError
                            }
                          >
                            {toString(errors.previousPostalCodeValue.message)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <div className="inline-buttons">
                  <Button
                    data-testid={
                      locator.pages.applicantEditResidence.form.cancelButton
                    }
                    variant="secondary"
                    fullWidth
                    onClick={finishEditing}
                  >
                    {t('ApplicantEditResidencePage.cancel')}
                  </Button>

                  <Button
                    data-testid={
                      locator.pages.applicantEditResidence.form.submitButton
                    }
                    disabled={!isValid}
                    loader={isSyncingResidence}
                    fullWidth
                    submit
                  >
                    {t('ApplicantEditResidencePage.save')}
                  </Button>
                </div>
              </div>
            </div>
            <footer
              data-testid={locator.pages.applicantEditResidence.footer.root}
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </Box>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'ApplicantEditResidencePage';
